// Temperature chart on uPlot: one line per probe; for rollups (5-minute, hourly, daily) the average with
// its min–max band, so a short excursion inside a bucket stays visible. Alarm thresholds are dashed
// lines, door openings shaded spans, and a missing stretch of data (device offline) a gap, not a line.

import type { SeriesResponse } from '@cck/schema';
import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { buildChartData, decimalsOf } from '../lib/chartData.ts';
import { themeColor, useTheme } from './ui.tsx';
import type { Lang } from '../lib/format.ts';

export const PROBE_COLORS = ['#2a7fb8', '#059669', '#9333ea', '#ea580c', '#db2777', '#0891b2'];

export interface Threshold {
  value: number;
  color: string;
  label: string;
}

const FR_NAMES = {
  MMMM: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
  MMM: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
  WWWW: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
  WWW: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
};

// Axis stamps, 24-hour and day-first in both languages (uPlot's defaults are American): [tick
// increment, format, then the formats used where the year, month, day… change, and a mode flag].
const D = 86_400;
const H = 3600;
const TIME_STAMPS: (string | number | null)[][] = [
  [365 * D, '{YYYY}', null, null, null, null, null, null, 1],
  [28 * D, '{MMM}', '\n{YYYY}', null, null, null, null, null, 1],
  [D, '{D} {MMM}', '\n{YYYY}', null, null, null, null, null, 1],
  [H, '{HH}:{mm}', '\n{D} {MMM} {YYYY}', null, '\n{D} {MMM}', null, null, null, 1],
  [60, '{HH}:{mm}', '\n{D} {MMM} {YYYY}', null, '\n{D} {MMM}', null, null, null, 1],
  [1, '{HH}:{mm}:{ss}', '\n{D} {MMM} {YYYY}', null, '\n{D} {MMM}', null, null, null, 1],
];

function hexToRgba(hex: string, a: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export interface TemperatureChartProps {
  data: SeriesResponse;
  labels: Record<string, string>;
  thresholds?: Threshold[];
  /** Door-open spans, Unix seconds; `to` null while still open. */
  doorSpans?: { from: number; to: number | null }[];
  timeZone: string;
  lang: Lang;
  height?: number;
}

export function TemperatureChart({ data, labels, thresholds = [], doorSpans = [], timeZone, lang, height = 320 }:
  TemperatureChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);
  const chart = useMemo(() => buildChartData(data), [data]);
  const theme = useTheme();
  const shape = `${chart.rollup}|${chart.lines.map((l) => l.probeId).join(',')}|${timeZone}|${lang}|${theme}`;
  const extras = useRef({ thresholds, doorSpans });
  extras.current = { thresholds, doorSpans };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const locale = lang === 'fr' ? 'fr-FR' : 'en-GB';
    const number = (v: number, digits: number) => v.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    const fmt = (v: number | null | undefined) => (v == null ? '—' : `${number(v, 1)} °C`);
    const series: uPlot.Series[] = [{ label: lang === 'fr' ? 'Heure' : 'Time', value: (_u, v) => (v == null ? '—'
      : new Date(v * 1000).toLocaleString(locale, { timeZone, dateStyle: 'short', timeStyle: 'short' })) }];
    const bands: uPlot.Band[] = [];
    const hidden: number[] = [];
    chart.lines.forEach((l, i) => {
      const color = PROBE_COLORS[i % PROBE_COLORS.length]!;
      const base = series.length;
      // For rollups the legend shows the bucket's average with its min–max range; the band's two edge
      // series stay out of the legend.
      series.push({ label: labels[l.probeId] ?? l.probeId.slice(0, 8), stroke: color, width: 1.75,
        value: (u, v, _s, idx) => (v == null || idx == null || !chart.rollup ? fmt(v)
          : `${fmt(v)} (${fmt(u.data[base + 1]![idx])} – ${fmt(u.data[base + 2]![idx])})`) });
      if (chart.rollup) {
        series.push({ stroke: hexToRgba(color, 0.35), width: 0.5 }, { stroke: hexToRgba(color, 0.35), width: 0.5 });
        bands.push({ series: [base + 2, base + 1], fill: hexToRgba(color, 0.12) });
        hidden.push(base + 1, base + 2);
      }
    });
    const opts: uPlot.Options = {
      width: el.clientWidth || 600,
      height,
      tzDate: (ts) => uPlot.tzDate(new Date(ts * 1000), timeZone),
      ...(lang === 'fr' ? { fmtDate: (tpl: string) => uPlot.fmtDate(tpl, FR_NAMES) } : {}),
      series,
      bands,
      legend: { live: true },
      cursor: { drag: { x: true, y: false } },
      scales: {
        y: {
          range: (u, min, max) => {
            const th = extras.current.thresholds.map((t) => t.value);
            const lo = Math.min(min ?? 0, ...th);
            const hi = Math.max(max ?? 10, ...th);
            const pad = Math.max(0.5, (hi - lo) * 0.08);
            void u;
            return [lo - pad, hi + pad];
          },
        },
      },
      axes: [
        { stroke: themeColor('slate-500'), grid: { stroke: themeColor('slate-100') }, values: TIME_STAMPS as uPlot.Axis.Values },
        { stroke: themeColor('slate-500'), grid: { stroke: themeColor('slate-100') }, size: 64,
          values: (_u, vals, _a, _s, incr) => vals.map((v) => `${number(v, decimalsOf(incr))} °C`) },
      ],
      hooks: {
        drawClear: [(u) => {
          const ctx = u.ctx;
          ctx.save();
          ctx.fillStyle = 'rgba(245, 158, 11, 0.14)';
          for (const s of extras.current.doorSpans) {
            const x0 = u.valToPos(s.from, 'x', true);
            const x1 = u.valToPos(s.to ?? Date.now() / 1000, 'x', true);
            const left = Math.max(x0, u.bbox.left);
            const right = Math.min(x1, u.bbox.left + u.bbox.width);
            if (right > left) ctx.fillRect(left, u.bbox.top, right - left, u.bbox.height);
          }
          ctx.restore();
        }],
        draw: [(u) => {
          const ctx = u.ctx;
          ctx.save();
          ctx.setLineDash([6, 4]);
          ctx.lineWidth = 1.25 * devicePixelRatio;
          ctx.font = `${11 * devicePixelRatio}px system-ui, sans-serif`;
          for (const t of extras.current.thresholds) {
            const y = u.valToPos(t.value, 'y', true);
            if (y < u.bbox.top || y > u.bbox.top + u.bbox.height) continue;
            ctx.strokeStyle = t.color;
            ctx.beginPath();
            ctx.moveTo(u.bbox.left, y);
            ctx.lineTo(u.bbox.left + u.bbox.width, y);
            ctx.stroke();
            // The label sits at the right end, above its line, on a light backing.
            const right = u.bbox.left + u.bbox.width - 6 * devicePixelRatio;
            const w = ctx.measureText(t.label).width;
            ctx.fillStyle = themeColor('white', '#ffffff');
            ctx.fillRect(right - w - 3 * devicePixelRatio, y - 16 * devicePixelRatio, w + 6 * devicePixelRatio, 13 * devicePixelRatio);
            ctx.fillStyle = t.color;
            ctx.textAlign = 'right';
            ctx.fillText(t.label, right, y - 6 * devicePixelRatio);
          }
          ctx.restore();
        }],
      },
    };
    const aligned: uPlot.AlignedData = [chart.x, ...chart.lines.flatMap((l) => (chart.rollup ? [l.avg, l.min, l.max] : [l.avg]))] as
      unknown as uPlot.AlignedData;
    plot.current = new uPlot(opts, aligned, el);
    el.querySelectorAll<HTMLElement>('.u-legend .u-series').forEach((row, i) => {
      if (hidden.includes(i)) row.style.display = 'none';
    });
    const ro = new ResizeObserver(() => plot.current?.setSize({ width: el.clientWidth, height }));
    ro.observe(el);
    return () => {
      ro.disconnect();
      plot.current?.destroy();
      plot.current = null;
    };
    // Rebuilt when the set of lines changes; data updates go through setData below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, height]);

  useEffect(() => {
    if (!plot.current) return;
    plot.current.setData([chart.x, ...chart.lines.flatMap((l) => (chart.rollup ? [l.avg, l.min, l.max] : [l.avg]))] as
      unknown as uPlot.AlignedData);
  }, [chart]);

  useEffect(() => {
    plot.current?.redraw(false);
  }, [thresholds, doorSpans]);

  return <div ref={ref} className="w-full" />;
}
